import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { Typography } from '@material-ui/core';

import { LOCALES_OBJ } from 'constants/base';

import { currencyFormatter } from 'utils/prices/formatter';
import {
  getSummeryPriceForActivitySeveralDays,
  getTotalPrice
} from 'utils/book-accommodation-and-activity/totalPrice';
import { getFormByCurrentLanguage } from 'utils/form-localization/formLocalization';

import useStyles from './styles';

const DAILY_PRICES = 'pricesDailyForm';

const AddOnsComponent = ({ addOns, currency }) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();

  const selectedLanguage = i18n.language;

  if (!addOns || !addOns.length) return null;

  const filledAddOns = addOns.filter(
    item => item.value && parseInt(item.value)
  );

  if (!filledAddOns || !filledAddOns.length) return null;

  return (
    <div className={classes.inputIndent}>
      <Typography variant="h3" className={classes.summaryTitle}>
        {t('Add-ons')}
      </Typography>
      {addOns.map((item, index) => {
        if (!item.value || !parseInt(item.value)) return;
        const price = currencyFormatter(item[currency]);
        const total = currencyFormatter(parseInt(item.value) * item[currency]);
        const form = getFormByCurrentLanguage(
          selectedLanguage,
          item,
          item['Norwegian']
        );
        return (
          <div key={index} className={classes.addOnsSummaryWrapper}>
            <Typography variant="body1" className={classes.summaryActivityName}>
              {form.title} • {currency} {price}
            </Typography>
            <div className={classes.summaryPricesList}>
              <div className={classes.summaryPricesItem}>
                <Typography variant="h3">
                  {currency}{' '}
                  <span className={classes.summaryPrice}>{price}</span> ×{' '}
                  {item.value}
                </Typography>
                <Typography variant="h3">
                  {currency}{' '}
                  <span className={classes.summaryPrice}>{total}</span>
                </Typography>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MandatoryFeesComponent = ({ mandatoryFees, currency }) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();

  const selectedLanguage = i18n.language;

  if (!mandatoryFees || !mandatoryFees.length) return null;

  const filledMandatoryFees = mandatoryFees.filter(
    item => item && item[currency] && parseFloat(item[currency])
  );

  if (!filledMandatoryFees || !filledMandatoryFees.length) return null;

  return (
    <div className={classes.inputIndent}>
      <Typography variant="h3" className={classes.summaryTitle}>
        {t('Mandatory-Fees')}
      </Typography>
      {mandatoryFees.map((item, index) => {
        if (!item[currency] || !parseInt(item[currency])) return;
        const price = currencyFormatter(item[currency]);
        const form = getFormByCurrentLanguage(
          selectedLanguage,
          item,
          item['Norwegian']
        );
        return (
          <div key={index} className={classes.addOnsSummaryWrapper}>
            <Typography variant="body1" className={classes.summaryActivityName}>
              {form.title} • {currency} {price}
            </Typography>
            <div className={classes.summaryPricesList}>
              <div className={classes.summaryPricesItem}>
                <Typography variant="h3">
                </Typography>
                <Typography variant="h3">
                  {currency}{' '}
                  <span className={classes.summaryPrice}>{price}</span>
                </Typography>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};


const BookingSummary = ({
  activity,
  bookActivity,
  relatedItems,
  generalSectionTitle
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();

  const selectedLanguage = i18n.language;

  const getActivitySummaryDate = () => {
    if (
      bookActivity.dates &&
      bookActivity.dates.startDate &&
      bookActivity.dates.endDate
    ) {
      const startDate = moment(bookActivity.dates.startDate)
        .locale(LOCALES_OBJ[selectedLanguage])
        .format('MMM DD, YYYY');
      const endDate = moment(bookActivity.dates.endDate)
        .locale(LOCALES_OBJ[selectedLanguage])
        .format('MMM DD, YYYY');
      const checkInTime = moment(activity.generalForm.checkInAfter).format(
        'HH:mm'
      );
      const checkOutTime = moment(activity.generalForm.checkOutBefore).format(
        'HH:mm'
      );

      return `${startDate} – ${endDate} • ${t('Check-in')}: ${checkInTime}, ${t(
        'Check-out'
      )}: ${checkOutTime}`;
    }

    const startingDate = bookActivity.startingDate;
    const startTime = bookActivity.startTime;
    let dateStr = ``;

    if (startingDate) {
      dateStr = `${moment(bookActivity.startingDate)
        .locale(LOCALES_OBJ[selectedLanguage])
        .format('MMM DD, YYYY')}`;
    }

    if (startTime) {
      dateStr = `${dateStr} • ${t('Start time')}: ${startTime.startTime} • ${startTime.duration
        } ${t(startTime.timeType)}`;
    }

    return dateStr;
  };

  const isActivitySummary = () => {
    let isShow = false;

    bookActivity.participants.map(item => {
      if (!item.participants || !parseInt(item.participants)) return;
      isShow = true;
    });

    return isShow;
  };

  const isAddOnsSummary = () => {
    let isShow = false;

    bookActivity.addOns.map(item => {
      if (!item.value || !parseInt(item.value)) return;
      isShow = true;
    });

    return isShow;
  };

  const isMandatoryFeesSummary = () => {
    let isShow = false;
    if (bookActivity?.mandatoryFees?.length > 0)
      isShow = true;
    return isShow;
  };

  const getTotalFixedSummaryPrice = (
    bookedItem,
    item,
    price,
    isParticipants
  ) => {
    if (
      bookedItem.dates &&
      bookedItem.dates.startDate &&
      bookedItem.dates.endDate
    ) {
      const currency = bookActivity.currency;
      const dates = bookedItem.dates;
      const nights = dates.endDate.diff(dates.startDate, 'days');
      const participants = isParticipants ? item.participants : 1;
      return currencyFormatter(
        parseInt(nights) * item[currency] * participants
      );
    } else {
      return price;
    }
  };
  if (!isActivitySummary() && !isAddOnsSummary() && !relatedItems.length) {
    return null;
  }

  const relatedItem = relatedItems.length ? relatedItems[0] : null;
  const relatedAddOns = [];
  const relatedMandatoryFees = [];
  if (relatedItem) {
    relatedItems.map(item => relatedAddOns.push(...item.addOns));
  }
  if (relatedItem) {
    relatedItems.forEach(item => {
      if (item.mandatoryFees) {
        relatedMandatoryFees.push(...item.mandatoryFees); // Spread mandatoryFees if it exists
      } else {
        relatedMandatoryFees.push(null); // Push null if mandatoryFees does not exist
      }
    });
  }
  return (
    <div className={classes.summeryWrapper}>
      <Typography variant="h2" className={classes.inputIndent}>
        {t('Booking summary')}
      </Typography>
      {isActivitySummary() && (
        <div className={classes.inputIndent}>
          <Typography variant="h3" className={classes.summaryTitle}>
            {t(generalSectionTitle)}
          </Typography>
          <Typography variant="body1" className={classes.summaryActivityName}>
            {
              getFormByCurrentLanguage(
                selectedLanguage,
                activity.generalForm,
                activity['generalForm-Norwegian']
              ).title
            }
          </Typography>
          <Typography variant="body1" className={classes.summaryActivityInfo}>
            {getActivitySummaryDate()}
          </Typography>
          <div className={classes.summaryPricesList}>
            {bookActivity.participants.map(item => {
              if (!item.participants || !parseInt(item.participants)) return;
              const price = currencyFormatter(item.currencyValue);
              const isAccommodationDailyPrice =
                activity.relatedActivitiesForm &&
                activity.pricesType === DAILY_PRICES;
              let summaryPrice = isAccommodationDailyPrice
                ? item.currencyValue
                : item.currencyValue * parseInt(item.participants);
              const nights =
                bookActivity.dates && bookActivity.dates.endDate
                  ? bookActivity.dates.endDate.diff(
                    bookActivity.dates.startDate,
                    'days'
                  )
                  : 0;
              const pluralNights = nights > 1 ? t('nights') : t('night');
              if (
                activity.pricesType === 'pricesFixedForm' ||
                activity.pricesType === 'pricesCategoryForm' ||
                activity.pricesType === 'futurePricesForm'
              ) {
                summaryPrice = getTotalFixedSummaryPrice(
                  bookActivity,
                  item,
                  price,
                  activity.pricesType === 'pricesCategoryForm'
                );
              }
              summaryPrice = currencyFormatter(
                getSummeryPriceForActivitySeveralDays(
                  bookActivity.multiDates,
                  summaryPrice
                )
              );
              return (
                <div key={item.id} className={classes.summaryPricesItem}>
                  {isAccommodationDailyPrice ? (
                    <Typography variant="body1">
                      {item.participants} {t(item.participant) || t('guests')}{' '}
                      {!!nights && (
                        <>
                          × {nights} {pluralNights}
                        </>
                      )}
                    </Typography>
                  ) : (
                    <>
                      {activity.pricesType === 'pricesFixedForm' || activity.pricesType === 'futurePricesForm' ? (
                        <Typography variant="body1">
                          <span className={classes.summaryCurrency}>
                            {item.currency}
                          </span>{' '}
                          {price}{' '}
                          {!!nights && (
                            <>
                              × {nights} {pluralNights}
                            </>
                          )}
                        </Typography>
                      ) : (
                        <Typography variant="body1">
                          <span className={classes.summaryCurrency}>
                            {item.currency}
                          </span>{' '}
                          {price} × {item.participants}{' '}
                          {t(item.participant) || t('guests')}{' '}
                          {!!nights && (
                            <>
                              × {nights} {pluralNights}
                            </>
                          )}
                        </Typography>
                      )}
                    </>
                  )}
                  <Typography variant="body1">
                    {item.currency}{' '}
                    <span className={classes.summaryPrice}>{summaryPrice}</span>
                  </Typography>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {isMandatoryFeesSummary() && (
        <MandatoryFeesComponent
          mandatoryFees={bookActivity.mandatoryFees}
          currency={bookActivity.currency}
        />
      )}

      {isAddOnsSummary() && (
        <AddOnsComponent
          addOns={bookActivity.addOns}
          currency={bookActivity.currency}
        />
      )}

      {!!relatedItems.length && (
        <>
          <div className={classes.inputIndent}>
            <Typography variant="h3" className={classes.summaryTitle}>
              {relatedItem.dates ? t('Accommodation') : t('Activities')}
            </Typography>

            {relatedItems.map((relatedItem, index) => (
              <div key={index} className={classes.relatedItem}>
                <Typography
                  variant="body1"
                  className={classes.summaryActivityName}
                >
                  {selectedLanguage === 'Norway' &&
                    Object.keys(relatedItem['Norwegian']).length
                    ? relatedItem['Norwegian'].title
                    : relatedItem.title}
                </Typography>
                <Typography
                  variant="body1"
                  className={classes.summaryActivityInfo}
                >
                  {relatedItem.dates && (
                    <>
                      {moment(relatedItem.dates.startDate)
                        .locale(LOCALES_OBJ[selectedLanguage])
                        .format('MMM DD, YYYY')}{' '}
                      –{' '}
                      {moment(relatedItem.dates.endDate)
                        .locale(LOCALES_OBJ[selectedLanguage])
                        .format('MMM DD, YYYY')}
                    </>
                  )}
                  {relatedItem.startingDate && (
                    <>
                      {moment(relatedItem.startingDate)
                        .locale(LOCALES_OBJ[selectedLanguage])
                        .format('MMM DD, YYYY')}{' '}
                      {relatedItem.startTime && (
                        <>
                          {'• '}
                          {relatedItem.startTime.startTime} •{' '}
                          {relatedItem.startTime.duration}{' '}
                          {t(relatedItem.startTime.timeType)}
                        </>
                      )}
                    </>
                  )}
                </Typography>
                <div className={classes.summaryPricesList}>
                  {relatedItem.participants.map(item => {
                    if (!item.participants || !parseInt(item.participants))
                      return;
                    const price = currencyFormatter(
                      item[bookActivity.currency]
                    );
                    const isAccommodationDailyPrice =
                      relatedItem.accommodation &&
                      relatedItem.pricesType === DAILY_PRICES;
                    let summaryPrice = isAccommodationDailyPrice
                      ? item[bookActivity.currency]
                      : item[bookActivity.currency] *
                      parseInt(item.participants);
                    const nights = relatedItem.dates
                      ? relatedItem.dates.endDate.diff(
                        relatedItem.dates.startDate,
                        'days'
                      )
                      : 0;
                    const pluralNights = nights > 1 ? t('nights') : t('night');
                    if (
                      relatedItem.pricesType === 'pricesFixedForm' ||
                      relatedItem.pricesType === 'pricesCategoryForm' 
                      || relatedItem.pricesType === 'futurePricesForm'
                    ) {
                      summaryPrice = getTotalFixedSummaryPrice(
                        relatedItem,
                        item,
                        price,
                        relatedItem.pricesType === 'pricesCategoryForm'
                      );
                    }
                    summaryPrice = currencyFormatter(
                      getSummeryPriceForActivitySeveralDays(
                        relatedItem.multiDates,
                        summaryPrice
                      )
                    );
                    return (
                      <div key={item.id} className={classes.summaryPricesItem}>
                        {isAccommodationDailyPrice ? (
                          <Typography variant="h3">
                            {item.participants}{' '}
                            {t(item.participant) || t('guests')}{' '}
                            {!!nights && (
                              <>
                                × {nights} {pluralNights}
                              </>
                            )}
                          </Typography>
                        ) : (
                          <Typography variant="h3">
                            {bookActivity.currency}{' '}
                            <span className={classes.summaryPrice}>
                              {price}
                            </span>{' '}
                            × {item.participants}{' '}
                            {t(item.participant) || t('guests')}{' '}
                            {!!nights && (
                              <>
                                × {nights} {pluralNights}
                              </>
                            )}
                          </Typography>
                        )}
                        <Typography variant="h3">
                          {bookActivity.currency}{' '}
                          <span className={classes.summaryPrice}>
                            {summaryPrice}
                          </span>
                        </Typography>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <MandatoryFeesComponent
            mandatoryFees={relatedMandatoryFees}
            currency={bookActivity.currency}
          />

          <AddOnsComponent
            addOns={relatedAddOns}
            currency={bookActivity.currency}
          />
        </>
      )}

      <div className={classes.summaryTotal}>
        <Typography variant="h2" className={classes.fontWeight}>
          {t('Total')}
        </Typography>
        <Typography variant="h3" className={classes.summaryTotalPriceBlock}>
          {bookActivity.currency}{' '}
          <span className={classes.summaryTotalPrice}>
            {currencyFormatter(
              getTotalPrice(
                activity,
                bookActivity.dates,
                bookActivity.participants,
                bookActivity.addOns,
                bookActivity.mandatoryFees,
                relatedItems,
                bookActivity.currency,
                bookActivity.multiDates
              )
            )}
          </span>
        </Typography>
      </div>
    </div>
  );
};


export default BookingSummary;
